/**
 * `<DetailPane>` — the COMPENDIUM **record sheet**: the brutalist cross-linked detail view for one
 * compendium record (frame, weapon, talent, license, …). Extracted from {@link CompendiumArchive}
 * so the SAME rich rendering can be reused outside the archive — notably the editor's "＋ add"
 * pickers, which read a record's full sheet before allocating it (see {@link CompendiumRecordReader}).
 *
 * Pure presentation: it takes a prebuilt {@link Detail} (from {@link buildDetail}) and an `onNav`
 * for cross-reference links, and owns none of the navigation/history/state — that belongs to the
 * host (the window's back-stack, or the reader's in-place preview). Manufacturer recolor flows
 * through `<ThemeScope>` → `var(--editor-accent)`, exactly as in the archive.
 *
 * The body sections render bundled compendium HTML (`<br>`, `<ul>`, `<b>`, …) via
 * `dangerouslySetInnerHTML`. That content is **static compendium data** (`@massif/lancer-data`),
 * never user input — there is no untrusted HTML on this path.
 */
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../lib/Icon";
import { useEffect, useState, type CSSProperties } from "react";
import { splitProseParagraphs } from "../../lib/prose";
import { ThemeScope } from "../../lib/editor/ui/primitives";
import {
  buildDetail,
  type Detail,
  type LinkItem,
  type NpcActionView,
} from "./compendiumView";
import { NPC_TIERS, type NpcTier } from "../../lib/npcTierText";
import type { NpcWeaponView } from "./npcWeapon";
import type { NpcDeployableView } from "./npcDeployable";
import { CompendiumSource, type Ref } from "./compendiumData";
import { ValText, markValSlots } from "./valSlot";
import "./compendium.css";

const MONO = "'JetBrains Mono', monospace";

/**
 * Renders one compendium body. Paragraphs arrive `<br>`-delimited rather than `<p>`-wrapped (see
 * {@link splitProseParagraphs}), so they're split into real elements here and spaced by
 * `.compendium-para` — every surface that mounts this sheet, the archive and the editor's `＋ add`
 * pickers alike, gets the gap from this one seam.
 */
function Prose({ html, style }: { html: string; style?: CSSProperties }) {
  const paragraphs = splitProseParagraphs(html);
  if (paragraphs.length < 2) {
    return <div className="compendium-prose" style={style} dangerouslySetInnerHTML={{ __html: html }} />;
  }
  return (
    <div className="compendium-prose" style={style}>
      {paragraphs.map((p, i) => (
        <div key={i} className="compendium-para" dangerouslySetInnerHTML={{ __html: p }} />
      ))}
    </div>
  );
}

// ---- detail sheet -------------------------------------------------------------------

const sectionLabel = (text: string, color = "#888"): CSSProperties => ({ fontFamily: MONO, fontSize: 9, letterSpacing: 2, color, marginBottom: 9 });
const rowLabelCell: CSSProperties = { flex: "none", borderRight: "2px solid #111", padding: "12px 16px", fontFamily: MONO, fontSize: 10, letterSpacing: 1, color: "#888" };

function LinkChip({ it, onNav }: { it: LinkItem; onNav: (ref: Ref) => void }) {
  return (
    <button
      type="button"
      className="compendium-hover-link"
      onClick={() => onNav({ type: it.type, id: it.id })}
      style={{ display: "flex", alignItems: "center", gap: 8, border: "1.5px solid #111", background: "#fff", cursor: "pointer", padding: "6px 10px", textAlign: "left" }}
    >
      <span style={{ width: 6, height: 6, background: it.dot }} />
      <span style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>{it.name}</span>
      <span style={{ fontFamily: MONO, fontSize: 8, color: "#999", letterSpacing: ".5px" }}>{it.kind}<Icon icon={faUpRightFromSquare} className="icon-trail" /></span>
    </button>
  );
}

/**
 * The T1/T2/T3 toggle. Every number on an NPC record — the class stat array, a weapon's damage and
 * attack bonus, a deployable's HP, and the values interpolated into rules text — is per-tier, and
 * the developer's call (2026-09-05) was to show ONE tier at a time behind this toggle rather than
 * three side-by-side columns. A GM mid-encounter is running a tier, not comparing them.
 */
function NpcTierToggle({ tier, onTier }: { tier: NpcTier; onTier: (t: NpcTier) => void }) {
  return (
    <div style={{ display: "flex", gap: 4, marginTop: 9 }} role="group" aria-label="NPC tier">
      {NPC_TIERS.map((t) => {
        const on = t === tier;
        return (
          <button
            key={t}
            type="button"
            aria-pressed={on}
            aria-label={`Tier ${t}`}
            onClick={() => onTier(t)}
            style={{
              fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px",
              padding: "3px 9px", cursor: "pointer",
              border: "1px solid rgba(255,255,255,.45)",
              color: on ? "#111" : "#fff",
              background: on ? "#fff" : "transparent",
            }}
          >
            T{t}
          </button>
        );
      })}
    </div>
  );
}

/**
 * `tier`/`onTier` are optional so a host that never shows NPC records need not care; when a host
 * supplies them the toggle renders on any record built at a tier (`d.npcTier` is set only for the
 * three NPC kinds). Both hosts DO supply them — the archive window and `CompendiumRecordReader` —
 * which is what item 3.3 asks for: the pane stays pure presentation and the state lives in the
 * host, so neither host can drift into having the control while the other lacks it.
 */
export function DetailPane({ d, onNav, tier }: { d: Detail; onNav: (ref: Ref) => void; tier?: { value: NpcTier; onTier: (t: NpcTier) => void } }) {
  // Route the manufacturer recolor through the shared `<ThemeScope>` seam: it sets
  // `--editor-accent`/`--editor-accent-dim` on this wrapper so every accent inside the detail
  // (header tint, maker wordmark underline, the per-body accent reads) resolves to the item's
  // real brand color with no per-leaf prop threading. A tinted header paints
  // `var(--editor-accent)`; an un-tinted one (non-maker record) keeps the ink ground.
  // `key={d.ref}` remounts this wrapper on each detail load so the CSS detail-in animation
  // (a brief fade + lift) re-fires per record. The animation is disabled under
  // `prefers-reduced-motion` (compendium.css).
  return (
    <ThemeScope key={d.ref} accent={d.accent} className="compendium-detail-in">
      {/* header — for a frame in a wide pane, container-query CSS collapses this to a thin
          manufacturer-color stripe and the name/tags re-appear overlaid on the chassis hero. */}
      <div className={d.kind === "frame" ? "compendium-frame-header" : undefined} style={{ position: "relative", overflow: "hidden", borderBottom: "2px solid #111", background: d.tinted ? "var(--editor-accent)" : d.headBg, color: d.headFg, padding: "20px 22px" }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.13, background: `repeating-linear-gradient(0deg,${d.headFg},${d.headFg} 1px,transparent 1px,transparent 4px)` }} />
        <div className="compendium-frame-header-body" style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: 2, color: d.headSub }}>{d.kicker}</div>
            <div style={{ fontSize: 27, fontWeight: 800, letterSpacing: "-.6px", lineHeight: 1.02, marginTop: 5, color: d.headFg, wordBreak: "break-word" }}><ValText text={d.name} /></div>
            {d.chips.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 11 }}>
                {d.chips.map((c, i) => (
                  <span key={i} style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px", padding: "3px 8px", border: `1px solid ${c.bd}`, color: c.fg, background: c.bg }}>{c.t}</span>
                ))}
              </div>
            )}
            {d.npcTier !== undefined && tier && <NpcTierToggle tier={tier.value} onTier={tier.onTier} />}
          </div>
          <div style={{ flex: "none", textAlign: "right" }}>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: d.headSub }}>FROM PACK</div>
            <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: d.headFg, marginTop: 3, border: `1px solid ${d.headSub}`, padding: "3px 7px" }}>{d.pack}</div>
          </div>
        </div>
      </div>

      {d.kind === "frame" && <FrameBody d={d} onNav={onNav} />}
      {d.kind === "weapon" && <WeaponBody d={d} onNav={onNav} />}
      {(d.kind === "system" || d.kind === "mod") && <SystemBody d={d} onNav={onNav} />}
      {d.kind === "talent" && <TalentBody d={d} />}
      {d.kind === "license" && <LicenseBody d={d} onNav={onNav} />}
      {d.kind === "skill" && <SkillBody d={d} />}
      {d.kind === "pilot_gear" && <GearBody d={d} />}
      {d.kind === "core_bonus" && <CoreBonusBody d={d} onNav={onNav} />}
      {d.kind === "tag" && <TagBody d={d} onNav={onNav} />}
      {d.kind === "manufacturer" && <ManufacturerBody d={d} onNav={onNav} />}
      {d.kind === "background" && <BackgroundBody d={d} onNav={onNav} />}
      {d.kind === "npc_class" && <NpcClassBody d={d} onNav={onNav} />}
      {d.kind === "npc_template" && <NpcTemplateBody d={d} onNav={onNav} />}
      {d.kind === "npc_feature" && <NpcFeatureBody d={d} onNav={onNav} />}

      <div style={{ padding: "11px 18px", borderTop: "1px dashed #ccc", fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: "#b3ad9f" }}>
        REF · {d.ref} · OMNINET ARCHIVE · READ-ONLY
      </div>
    </ThemeScope>
  );
}

function MakerLink({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  if (!d.mfrId) return null;
  return (
    <button type="button" onClick={() => onNav({ type: "manufacturer", id: d.mfrId! })} style={{ border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 9, padding: 0, textAlign: "left" }}>
      <span style={{ width: 13, height: 13, background: "var(--editor-accent)" }} />
      <span style={{ fontSize: 16, fontWeight: 800, color: "#111", borderBottom: "2px solid var(--editor-accent)" }}>{d.mfrName}</span>
    </button>
  );
}

/**
 * The frame detail's chassis art — a large full-width hero rendering the id-keyed image from
 * our local `public/frames/` mirror, falling back to the hatched CHASSIS RENDER panel when a
 * frame has no mirrored image or the load fails. Tracks the failed src so
 * selecting a new frame retries (the component instance is reused across selections, so a plain
 * boolean would stick).
 *
 * In a wide pane (container query) the colored detail header collapses to a stripe and this hero
 * carries the chassis name + tags overlaid over a bottom scrim; the `.compendium-chassis-overlay`
 * is `display:none` until then, so the narrow pane keeps the stacked colored header instead.
 * An NPC class reuses the hero for its class art with `overlay={false}`: its header never
 * collapses, so an overlay would print the name twice.
 */
function ChassisRender({ d, overlay = true }: { d: Detail; overlay?: boolean }) {
  const url = d.artUrl;
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const show = url != null && failedSrc !== url;
  return (
    <div
      className="compendium-chassis-hero"
      style={{
        position: "relative",
        overflow: "hidden",
        borderBottom: "2px solid #111",
        background: "repeating-linear-gradient(45deg,#f2f0ea,#f2f0ea 8px,#e7e4dc 8px,#e7e4dc 16px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        minHeight: 320,
        padding: show ? "20px 16px" : 0,
      }}
    >
      {/* schematic layer: the PNG alpha masks an accent-tinted grid, so the mech silhouette
          reads as a wireframe. It sweeps in first (compendium.css), then the real image sweeps
          in over it. inset matches the hero's `show` padding so the mask aligns with the image. */}
      {show && (
        <div
          className="compendium-chassis-grid"
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: "20px 16px",
            zIndex: 0,
            pointerEvents: "none",
            backgroundColor: "transparent",
            backgroundImage:
              "repeating-linear-gradient(0deg, transparent 0 7px, var(--editor-accent) 7px 8px), repeating-linear-gradient(90deg, transparent 0 7px, var(--editor-accent) 7px 8px)",
            WebkitMaskImage: `url("${url}")`,
            maskImage: `url("${url}")`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
            WebkitMaskSize: "contain",
            maskSize: "contain",
          }}
        />
      )}
      {show ? (
        <img
          className="compendium-chassis-img"
          src={url}
          alt=""
          onError={() => setFailedSrc(url)}
          style={{ position: "relative", zIndex: 1, maxWidth: "100%", maxHeight: 420, objectFit: "contain" }}
        />
      ) : (
        <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: 2, color: "#9a9486", lineHeight: 1.9 }}>
          CHASSIS
          <br />
          RENDER
        </span>
      )}
      {/* name/tags overlay — revealed only in a wide pane (compendium.css container query) */}
      {overlay && (
        <div
          className="compendium-chassis-overlay"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "flex-start",
            textAlign: "left",
            padding: "18px 22px",
            pointerEvents: "none",
            background: "linear-gradient(to top, rgba(0,0,0,.6), rgba(0,0,0,.16) 34%, rgba(0,0,0,0) 58%)",
          }}
        >
          <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: 2, color: "rgba(255,255,255,.72)" }}>{d.kicker}</div>
          <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: "-.6px", lineHeight: 1.02, marginTop: 5, color: "#fff", textShadow: "0 2px 10px rgba(0,0,0,.55)", wordBreak: "break-word" }}>{d.name}</div>
          {d.chips.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 11 }}>
              {d.chips.map((c, i) => (
                <span key={i} style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px", padding: "3px 8px", border: `1px solid ${c.bd}`, color: c.fg, background: c.bg }}>{c.t}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FrameBody({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  return (
    <>
      <ChassisRender d={d} />
      <div style={{ padding: "13px 16px 5px", fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: "#888" }}>STAT BLOCK</div>
      <div style={{ borderTop: "1px solid #ddd", borderBottom: "2px solid #111" }}>
        {(d.statGroups ?? []).map((g, gi) => (
          <div key={g.label} style={{ display: "flex", borderBottom: gi < (d.statGroups ?? []).length - 1 ? "1px solid #ddd" : "none" }}>
            <div style={{ ...rowLabelCell, width: 118, display: "flex", alignItems: "center", fontSize: 9 }}>{g.label}</div>
            <div style={{ flex: 1, display: "grid", gridTemplateColumns: `repeat(${g.cells.length},1fr)`, minWidth: 0 }}>
              {g.cells.map((st, i) => (
                <div key={st.k} style={{ padding: "10px 4px", textAlign: "center", borderRight: i < g.cells.length - 1 ? "1px solid #ddd" : "none" }}>
                  <div style={{ fontSize: 19, fontWeight: 800, color: "#111", lineHeight: 1 }}>{st.v}</div>
                  <div style={{ fontFamily: MONO, fontSize: 7.5, letterSpacing: ".4px", color: "#888", marginTop: 5 }}>{st.k}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {(d.statTracks ?? []).length > 0 && (
          <div style={{ display: "flex", borderTop: "2px solid #111" }}>
            <div style={{ ...rowLabelCell, width: 118, display: "flex", alignItems: "center", fontSize: 9 }}>TRACKS</div>
            <div style={{ flex: 1, padding: "9px 16px", display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", fontFamily: MONO, fontSize: 9, letterSpacing: ".4px", color: "#888" }}>
              {(d.statTracks ?? []).map((st) => (
                <span key={st.k}>{st.k} <b style={{ color: "#111", fontSize: 11 }}>{st.v}</b></span>
              ))}
            </div>
          </div>
        )}
      </div>
      {(d.mounts ?? []).length > 0 && (
        <div style={{ display: "flex", borderBottom: "2px solid #111" }}>
          <div style={{ ...rowLabelCell, width: 118 }}>MOUNTS</div>
          <div style={{ flex: 1, padding: "12px 16px", display: "flex", flexWrap: "wrap", gap: 7 }}>
            {(d.mounts ?? []).map((m, i) => (
              <span key={i} style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: ".5px", color: "#111", background: "#f0ede4", border: "1.5px solid #111", padding: "4px 10px" }}>{m}</span>
            ))}
          </div>
        </div>
      )}
      <div style={{ padding: "13px 16px", borderBottom: "2px solid #111", minWidth: 0 }}>
        <div style={sectionLabel("")}>MANUFACTURER</div>
        <MakerLink d={d} onNav={onNav} />
        {d.descHtml && <Prose html={d.descHtml} style={{ marginTop: 11 }} />}
      </div>
    </>
  );
}

/** One damage pip — the coloured value + its type, shared by the record's DAMAGE panel and each profile's. */
function DamagePips({ damage }: { damage: NonNullable<Detail["damage"]> }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {damage.map((dm, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", border: "2px solid #111" }}>
          <span style={{ background: dm.col, color: "#fff", fontSize: 17, fontWeight: 800, padding: "5px 10px", lineHeight: 1 }}>{dm.val}</span>
          <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px", color: "#111", padding: "0 9px" }}>{dm.type}</span>
        </div>
      ))}
    </div>
  );
}

/** One range pip — the type label + its value, shared by the record's RANGE panel and each profile's. */
function RangePips({ range }: { range: NonNullable<Detail["range"]> }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {range.map((rg, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", border: "2px solid #111" }}>
          <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px", color: "#111", padding: "0 9px" }}>{rg.type}</span>
          <span style={{ background: "#111", color: "#fff", fontSize: 17, fontWeight: 800, padding: "5px 10px", lineHeight: 1 }}>{rg.val}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * The flat DAMAGE / RANGE panels. Each panel is drawn only when it has something to put in it, and
 * the pair drops out entirely when neither does — the same "don't render an empty box" rule the
 * profiles-only weapons already follow, applied one level down. Two core weapons have a range but
 * no top-level damage: the Latch Drone genuinely deals none, and the Gravity Gun's lives in its
 * `on_attack` rider (see {@link TriggeredBlock}). For both, an empty box under a DAMAGE heading
 * asserts "this weapon does zero damage" rather than "the number is somewhere else".
 */
function DamageRange({ d }: { d: Detail }) {
  const hasDamage = (d.damage?.length ?? 0) > 0;
  const hasRange = (d.range?.length ?? 0) > 0;
  if (!hasDamage && !hasRange) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: hasDamage && hasRange ? "repeat(2,1fr)" : "1fr", borderBottom: "2px solid #111" }}>
      {hasDamage && (
        <div style={{ borderRight: hasRange ? "2px solid #111" : undefined, padding: "13px 16px" }}>
          <div style={sectionLabel("")}>DAMAGE</div>
          <DamagePips damage={d.damage ?? []} />
        </div>
      )}
      {hasRange && (
        <div style={{ padding: "13px 16px" }}>
          <div style={sectionLabel("")}>RANGE</div>
          <RangePips range={d.range ?? []} />
        </div>
      )}
    </div>
  );
}

/**
 * The `profiles[]` block — one row per firing profile, each with its own DAMAGE/RANGE pips, its
 * own tags, and any profile-specific effect prose. It stands in for the flat DAMAGE/RANGE panels
 * on the five weapons whose stats live only in their profiles (Assault Cannon, Leviathan, Siege
 * Cannon, Apocalypse Rail, ZF4 SOLIDCORE) — without it those records show two empty panels.
 */
function WeaponProfilesBlock({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  if (!d.weaponProfiles || d.weaponProfiles.length === 0) return null;
  return (
    <div style={{ borderBottom: "2px solid #111" }}>
      <div style={{ padding: "15px 18px 0" }}>
        <div style={sectionLabel("", "#c0240f")}>PROFILES</div>
      </div>
      {d.weaponProfiles.map((p, i) => (
        <div key={i} style={{ padding: "0 18px 14px", marginTop: i === 0 ? 0 : 8 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#111", letterSpacing: ".2px", marginBottom: 8 }}>{p.name}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <DamagePips damage={p.damage} />
            <RangePips range={p.range} />
            {p.tags.map((t) => (
              <button
                key={t.id}
                type="button"
                className="compendium-hover-tag"
                onClick={() => onNav({ type: "tag", id: t.id })}
                style={{ border: "1.5px solid #111", background: "#fff", color: "#111", cursor: "pointer", padding: "4px 10px", fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: ".5px" }}
              >
                {t.label}<Icon icon={faUpRightFromSquare} className="icon-trail" />
              </button>
            ))}
          </div>
          {p.effectHtml && <Prose html={p.effectHtml} style={{ marginTop: 9 }} />}
        </div>
      ))}
    </div>
  );
}

/**
 * The `on_attack` / `on_hit` / `on_crit` block — a record's *conditional* riders, kept deliberately
 * apart from the flat DAMAGE panel above. The Gravity Gun deals its 1d6 only to whoever fails a
 * Hull save inside the blast; printing that number in DAMAGE would read as damage on a hit, which
 * is a lie a player acts on mid-fight. So the pips sit under the trigger that fires them, with the
 * gating conditions the data declares (the save, the AoE) as chips right beside them, and the
 * clause's own rules prose underneath. Wears the ACTIONS/AMMUNITION block idiom rather than a new
 * one: label bar, a chip row per entry, then prose.
 */
function TriggeredBlock({ d }: { d: Detail }) {
  if (!d.triggered || d.triggered.length === 0) return null;
  return (
    <div style={{ borderBottom: "2px solid #111" }}>
      <div style={{ padding: "15px 18px 0" }}>
        <div style={sectionLabel("", "#c0240f")}>TRIGGERED</div>
      </div>
      {d.triggered.map((t, i) => (
        <div key={i} style={{ padding: "0 18px 14px", marginTop: i === 0 ? 0 : 6 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 7, marginBottom: 7 }}>
            <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px", color: "#fff", background: "#111", padding: "3px 8px" }}>{t.trigger}</span>
            {t.activation && (
              <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px", color: "#111", border: "1px solid #111", padding: "3px 8px" }}>{t.activation.toUpperCase()}</span>
            )}
            {t.profile && <span style={{ fontSize: 13, fontWeight: 800, color: "#111", letterSpacing: ".2px" }}>{t.profile}</span>}
          </div>
          {(t.damage.length > 0 || t.quals.length > 0) && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 7, marginBottom: 8 }}>
              {t.damage.length > 0 && <DamagePips damage={t.damage} />}
              {t.quals.map((q) => (
                <span key={q} style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px", color: "#111", background: "#f0ede4", border: "1.5px solid #111", padding: "4px 9px" }}>{q}</span>
              ))}
            </div>
          )}
          {t.detailHtml && <Prose html={t.detailHtml} />}
        </div>
      ))}
    </div>
  );
}

function TagRow({ d, onNav, width }: { d: Detail; onNav: (ref: Ref) => void; width: number }) {
  if (!d.tags || d.tags.length === 0) return null;
  return (
    <div style={{ display: "flex", borderBottom: "2px solid #111" }}>
      <div style={{ ...rowLabelCell, width }}>TAGS</div>
      <div style={{ flex: 1, padding: "12px 16px", display: "flex", flexWrap: "wrap", gap: 7 }}>
        {d.tags.map((t) => (
          <button
            key={t.id}
            type="button"
            className="compendium-hover-tag"
            onClick={() => onNav({ type: "tag", id: t.id })}
            style={{ border: "1.5px solid #111", background: "#fff", color: "#111", cursor: "pointer", padding: "4px 10px", fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: ".5px" }}
          >
            {t.label}<Icon icon={faUpRightFromSquare} className="icon-trail" />
          </button>
        ))}
      </div>
    </div>
  );
}

function WeaponBody({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  // `DamageRange` self-suppresses the panels it has no content for, so a profiles-only weapon
  // (whose stats live in the block below) and a triggered-damage-only one both skip straight to
  // whichever block actually holds the numbers, instead of showing an empty box first.
  return (
    <>
      <DamageRange d={d} />
      <WeaponProfilesBlock d={d} onNav={onNav} />
      {d.effectHtml && (
        <div style={{ padding: "15px 18px", borderBottom: "2px solid #111" }}>
          <div style={sectionLabel("", "#c0240f")}>EFFECT</div>
          <Prose html={d.effectHtml} />
        </div>
      )}
      <TriggeredBlock d={d} />
      <TagRow d={d} onNav={onNav} width={90} />
      {d.descHtml && (
        <div style={{ padding: "15px 18px" }}>
          <Prose html={d.descHtml} />
        </div>
      )}
    </>
  );
}

function SystemBody({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  return (
    <>
      {d.allowed && d.allowed.length > 0 && (
        <div style={{ display: "flex", borderBottom: "2px solid #111" }}>
          <div style={{ ...rowLabelCell, width: 104 }}>FITS</div>
          <div style={{ flex: 1, padding: "12px 16px", display: "flex", flexWrap: "wrap", gap: 7 }}>
            {d.allowed.map((a, i) => (
              <span key={i} style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: ".5px", color: "#111", background: "#f0ede4", border: "1.5px solid #111", padding: "4px 9px" }}>{a}</span>
            ))}
          </div>
        </div>
      )}
      {d.effectHtml && (
        <div style={{ padding: "15px 18px", borderBottom: "2px solid #111" }}>
          <div style={sectionLabel("", "#c0240f")}>EFFECT</div>
          <Prose html={d.effectHtml} />
        </div>
      )}
      <ActionsBlock d={d} />
      <TriggeredBlock d={d} />
      <AmmoBlock d={d} />
      <TagRow d={d} onNav={onNav} width={104} />
      {d.descHtml && (
        <div style={{ padding: "15px 18px" }}>
          <Prose html={d.descHtml} />
        </div>
      )}
    </>
  );
}

/**
 * The system `ammo[]` block — the menu of selectable ammunition an Ammo Case offers. It sits
 * directly under EFFECT because the effect string is what points at it ("…one of the following
 * effects…at the listed cost"): without this block the record names a list it never shows. Each
 * row is one option — its charge cost, its name, and the rules prose for what it does to the attack.
 */
function AmmoBlock({ d }: { d: Detail }) {
  if (!d.ammo || d.ammo.length === 0) return null;
  return (
    <div style={{ borderBottom: "2px solid #111" }}>
      <div style={{ padding: "15px 18px 0" }}>
        <div style={sectionLabel("", "#c0240f")}>AMMUNITION</div>
      </div>
      {d.ammo.map((a, i) => (
        <div key={i} style={{ padding: "0 18px 14px", marginTop: i === 0 ? 0 : 6 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 7, marginBottom: 7 }}>
            <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px", color: "#fff", background: "#111", padding: "3px 8px" }}>
              {a.cost} {a.cost === 1 ? "CHARGE" : "CHARGES"}
            </span>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#111", letterSpacing: ".2px" }}>{a.name}</span>
          </div>
          {a.detailHtml && <Prose html={a.detailHtml} />}
        </div>
      ))}
    </div>
  );
}

/**
 * The system/mod `actions[]` block — for items whose rules live in activated actions rather
 * than the flat `effect` string (e.g. StunCrown's Quick action). Each action shows its
 * activation/frequency chips + optional name, an optional trigger line, then the rules prose.
 */
function ActionsBlock({ d }: { d: Detail }) {
  if (!d.actions || d.actions.length === 0) return null;
  return (
    <div style={{ borderBottom: "2px solid #111" }}>
      <div style={{ padding: "15px 18px 0" }}>
        <div style={sectionLabel("", "#c0240f")}>ACTIONS</div>
      </div>
      {d.actions.map((a, i) => (
        <div key={i} style={{ padding: "0 18px 14px", marginTop: i === 0 ? 0 : 6 }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 7, marginBottom: 7 }}>
            {a.activation && (
              <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px", color: "#fff", background: "#111", padding: "3px 8px" }}>{a.activation.toUpperCase()}</span>
            )}
            {a.frequency && (
              <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px", color: "#111", border: "1px solid #111", padding: "3px 8px" }}>{a.frequency.toUpperCase()}</span>
            )}
            {a.name && <span style={{ fontSize: 13, fontWeight: 800, color: "#111", letterSpacing: ".2px" }}>{a.name}</span>}
          </div>
          {a.triggerHtml && (
            <div style={{ marginBottom: 6 }}>
              <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: "#888" }}>TRIGGER · </span>
              <Prose html={a.triggerHtml} style={{ display: "inline" }} />
            </div>
          )}
          {a.detailHtml && <Prose html={a.detailHtml} />}
        </div>
      ))}
    </div>
  );
}

function TalentBody({ d }: { d: Detail }) {
  return (
    <>
      {d.descHtml && (
        <div style={{ padding: "14px 18px", borderBottom: "2px solid #111" }}>
          <Prose html={d.descHtml} />
        </div>
      )}
      {(d.ranks ?? []).map((r, i) => (
        <div key={i} style={{ display: "flex", borderBottom: "1px solid #e6e0d2" }}>
          <div style={{ flex: "none", width: 62, borderRight: "2px solid #111", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "14px 0", background: "#f6f4ee" }}>
            <div style={{ width: 34, height: 34, background: "#c0240f", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: MONO, fontSize: 15, fontWeight: 700 }}>{r.roman}</div>
          </div>
          <div style={{ flex: 1, padding: "13px 16px", minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#111", letterSpacing: ".2px" }}>{r.name}</div>
            {r.descHtml && <Prose html={r.descHtml} style={{ marginTop: 5 }} />}
          </div>
        </div>
      ))}
    </>
  );
}

function LicenseBody({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  return (
    <>
      {d.mfrId && (
        <div style={{ display: "flex", borderBottom: "2px solid #111" }}>
          <div style={{ ...rowLabelCell, width: 104 }}>MAKER</div>
          <div style={{ flex: 1, padding: "12px 16px" }}>
            <button type="button" onClick={() => onNav({ type: "manufacturer", id: d.mfrId! })} style={{ border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 9, padding: 0 }}>
              <span style={{ width: 12, height: 12, background: "var(--editor-accent)" }} />
              <span style={{ fontSize: 15, fontWeight: 800, color: "#111", borderBottom: "2px solid var(--editor-accent)" }}>{d.mfrName}</span>
            </button>
          </div>
        </div>
      )}
      <div style={{ padding: "13px 18px 5px", fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: "#888" }}>UNLOCK LADDER</div>
      {(d.unlocks ?? []).map((u, i) => (
        <div key={i} style={{ display: "flex", borderBottom: "1px solid #e6e0d2" }}>
          <div style={{ flex: "none", width: 62, borderRight: "2px solid #111", display: "flex", alignItems: "center", justifyContent: "center", padding: "14px 0", background: "#f6f4ee" }}>
            <div style={{ width: 34, height: 34, border: "2px solid #111", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: MONO, fontSize: 13, fontWeight: 700 }}>{u.rank}</div>
          </div>
          <div style={{ flex: 1, padding: "11px 14px", display: "flex", flexWrap: "wrap", gap: 8, alignContent: "center" }}>
            {u.items.map((it) => (
              <LinkChip key={`${it.type}/${it.id}`} it={it} onNav={onNav} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function SkillBody({ d }: { d: Detail }) {
  return (
    <>
      {d.descHtml && (
        <div style={{ padding: "16px 18px", borderBottom: "2px solid #111" }}>
          <Prose html={d.descHtml} />
        </div>
      )}
      {d.detailHtml && (
        <div style={{ padding: "14px 18px", background: "#f6f4ee" }}>
          <div style={sectionLabel("")}>WHEN YOU ROLL</div>
          <Prose html={d.detailHtml} />
        </div>
      )}
    </>
  );
}

function GearBody({ d }: { d: Detail }) {
  return (
    <>
      {d.profile && d.profile.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "13px 18px", borderBottom: "2px solid #111" }}>
          {d.profile.map((p, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", border: "2px solid #111" }}>
              <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: ".5px", color: "#111", padding: "0 9px" }}>{p.k}</span>
              <span style={{ background: "#111", color: "#fff", fontSize: 14, fontWeight: 800, padding: "5px 10px", lineHeight: 1 }}>{p.v}</span>
            </div>
          ))}
        </div>
      )}
      {d.effectHtml && (
        <div style={{ padding: "15px 18px", borderBottom: "2px solid #111" }}>
          <div style={sectionLabel("", "#c0240f")}>EFFECT</div>
          <Prose html={d.effectHtml} />
        </div>
      )}
      <ActionsBlock d={d} />
      {d.descHtml && (
        <div style={{ padding: "15px 18px" }}>
          <Prose html={d.descHtml} />
        </div>
      )}
    </>
  );
}

function CoreBonusBody({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  return (
    <>
      {d.mfrId && (
        <div style={{ display: "flex", borderBottom: "2px solid #111" }}>
          <div style={{ ...rowLabelCell, width: 104 }}>MAKER</div>
          <div style={{ flex: 1, padding: "12px 16px" }}>
            <button type="button" onClick={() => onNav({ type: "manufacturer", id: d.mfrId! })} style={{ border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 9, padding: 0 }}>
              <span style={{ width: 12, height: 12, background: "var(--editor-accent)" }} />
              <span style={{ fontSize: 15, fontWeight: 800, color: "#111", borderBottom: "2px solid var(--editor-accent)" }}>{d.mfrName}</span>
            </button>
          </div>
        </div>
      )}
      {d.effectHtml && (
        <div style={{ padding: "15px 18px", borderBottom: "2px solid #111" }}>
          <div style={sectionLabel("", "#c0240f")}>EFFECT</div>
          <Prose html={d.effectHtml} />
        </div>
      )}
      {d.mountedHtml && (
        <div style={{ padding: "15px 18px", borderBottom: "2px solid #111", background: "#f6f4ee" }}>
          <div style={sectionLabel("")}>MOUNTED EFFECT</div>
          <Prose html={d.mountedHtml} />
        </div>
      )}
      {d.descHtml && (
        <div style={{ padding: "15px 18px" }}>
          <Prose html={d.descHtml} />
        </div>
      )}
    </>
  );
}

function TagBody({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  return (
    <>
      {d.descHtml && (
        <div style={{ padding: 18, borderBottom: "2px solid #111" }}>
          {/* a tag definition's body is the one corpus text carrying unbound `{VAL}`s — marked
              before the paragraph split, which the injected spans pass through untouched */}
          <Prose html={markValSlots(d.descHtml)} style={{ fontSize: 14 }} />
        </div>
      )}
      {d.refs && d.refs.length > 0 && (
        <div style={{ padding: "14px 18px" }}>
          <div style={sectionLabel("")}>REFERENCED BY</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {d.refs.map((it) => (
              <LinkChip key={`${it.type}/${it.id}`} it={it} onNav={onNav} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function ManufacturerBody({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  return (
    <>
      {d.quoteHtml && (
        <div style={{ padding: 18, borderBottom: "2px solid #111", fontSize: 15, fontWeight: 600, fontStyle: "italic", color: "#333", lineHeight: 1.5 }}>
          <Prose html={`“${d.quoteHtml}”`} style={{ fontSize: 15, fontStyle: "italic", color: "#333" }} />
        </div>
      )}
      {d.descHtml && (
        <div style={{ padding: "16px 18px", borderBottom: "2px solid #111" }}>
          <Prose html={d.descHtml} />
        </div>
      )}
      {d.catalog && d.catalog.length > 0 && (
        <div style={{ padding: "14px 18px" }}>
          <div style={sectionLabel("")}>IN CATALOG</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {d.catalog.map((it) => (
              <LinkChip key={`${it.type}/${it.id}`} it={it} onNav={onNav} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function BackgroundBody({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  return (
    <>
      {d.descHtml && (
        <div style={{ padding: "16px 18px", borderBottom: "2px solid #111" }}>
          <Prose html={d.descHtml} />
        </div>
      )}
      {d.bgSkills && d.bgSkills.length > 0 && (
        <div style={{ padding: "14px 18px" }}>
          <div style={sectionLabel("")}>TYPICAL TRIGGERS</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {d.bgSkills.map((it) => (
              <button key={it.id} type="button" className="compendium-hover-tag" onClick={() => onNav({ type: "skill", id: it.id })} style={{ border: "1.5px solid #111", background: "#fff", color: "#111", cursor: "pointer", padding: "6px 10px", fontSize: 12, fontWeight: 700 }}>
                {it.name}<Icon icon={faUpRightFromSquare} className="icon-trail" />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ---- NPC bodies ---------------------------------------------------------------------

function FeatureGroups({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  if (!d.featureGroups || d.featureGroups.length === 0) return null;
  return (
    <>
      {d.featureGroups.map((g) => (
        <div key={g.label} style={{ padding: "14px 18px", borderBottom: "2px solid #111" }}>
          <div style={sectionLabel("")}>{g.label}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {g.items.map((it) => (
              <LinkChip key={`${it.type}/${it.id}`} it={it} onNav={onNav} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

/**
 * The class stat array at ONE tier — the developer's call (2026-09-05) was a T1/T2/T3 toggle
 * showing a single tier rather than three side-by-side columns, so the fight-running GM reads one
 * number per stat instead of picking a column out of a grid. Stats that move across tiers are
 * marked, so it is visible which numbers the toggle is actually changing.
 */
function NpcStatArray({ d }: { d: Detail }) {
  const rows = d.npcStats ?? [];
  if (rows.length === 0) return null;
  return (
    <>
      <div style={{ padding: "13px 18px 5px", fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: "#888" }}>
        STAT ARRAY · TIER {d.npcTier ?? 1}
      </div>
      <div style={{ borderBottom: "2px solid #111", borderTop: "1px solid #ddd" }}>
        {rows.map((row) => (
          <div
            key={row.k}
            style={{ display: "grid", gridTemplateColumns: "1fr minmax(64px,auto)", borderBottom: "1px solid #eee" }}
          >
            <div style={{ padding: "8px 16px", fontFamily: MONO, fontSize: 10, letterSpacing: ".5px", color: "#555" }}>
              {row.k}
              {/* A quiet marker, not a second column: it says "the toggle moves this" without
                  competing with the number itself. */}
              {row.varies && <span style={{ color: "#c0240f", marginLeft: 6 }} title="varies by tier">·</span>}
            </div>
            <div style={{ padding: "8px 16px", textAlign: "right", fontSize: 14, fontWeight: 800, color: "#111", borderLeft: "1px solid #eee" }}>
              {row.v}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function NpcClassBody({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  return (
    <>
      {d.artUrl && <ChassisRender d={d} overlay={false} />}
      {/* The one-line "what is this thing" summary, above the numbers — it is the fastest read on
          the sheet and the pack ships it precisely for that. */}
      {d.npcTerse && (
        <div style={{ padding: "13px 18px", borderBottom: "2px solid #111", fontSize: 13, fontWeight: 600, color: "#111" }}>
          {d.npcTerse}
        </div>
      )}
      <NpcStatArray d={d} />
      <FeatureGroups d={d} onNav={onNav} />
      {d.npcFlavorHtml && (
        <div style={{ padding: "15px 18px", borderBottom: "2px solid #111" }}>
          <Prose html={d.npcFlavorHtml} />
        </div>
      )}
      {d.npcTacticsHtml && (
        <div style={{ padding: "15px 18px", background: "#f6f4ee" }}>
          <div style={sectionLabel("", "#c0240f")}>TACTICS</div>
          <Prose html={d.npcTacticsHtml} />
        </div>
      )}
    </>
  );
}

function NpcTemplateBody({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  return (
    <>
      {d.descHtml && (
        <div style={{ padding: "15px 18px", borderBottom: "2px solid #111" }}>
          <Prose html={d.descHtml} />
        </div>
      )}
      {/* A rules qualifier on when the template's traits function at all ("Commander Traits do not
          function if the commander is Jammed"). It carries inline <b>, so it goes through Prose. */}
      {d.npcCaveatHtml && (
        <div style={{ padding: "13px 18px", borderBottom: "2px solid #111" }}>
          <div style={sectionLabel("", "#c0240f")}>CAVEAT</div>
          <Prose html={d.npcCaveatHtml} />
        </div>
      )}
      <FeatureGroups d={d} onNav={onNav} />
      {d.npcTacticsHtml && (
        <div style={{ padding: "15px 18px", background: "#f6f4ee" }}>
          <div style={sectionLabel("", "#c0240f")}>TACTICS</div>
          <Prose html={d.npcTacticsHtml} />
        </div>
      )}
    </>
  );
}

/**
 * One entry of a v3 feature's `actions[]`.
 *
 * The metadata line is the reason an action block still earns its place on the ~17 features that
 * carry BOTH an `effect` and an `actions[]` whose `detail` restates it: prose never says whether
 * the thing is Quick or Full, how often it may be used, or what triggers it. Those three facts
 * only exist here.
 */
function NpcActionBlock({ a }: { a: NpcActionView }) {
  const meta = [a.activation, a.frequency].filter((x): x is string => !!x);
  return (
    <div style={{ padding: "13px 18px", borderBottom: "2px solid #111" }}>
      <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: a.trigger || a.detailHtml || a.effectHtml ? 8 : 0 }}>
        {a.name && (
          <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: 1.5, fontWeight: 700 }}>
            {a.name.toUpperCase()}
          </div>
        )}
        {meta.map((m) => (
          <span
            key={m}
            style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: "#fff", background: "#c0240f", padding: "2px 6px" }}
          >
            {m.toUpperCase()}
          </span>
        ))}
      </div>
      {a.trigger && (
        <div style={{ marginBottom: a.detailHtml || a.effectHtml ? 8 : 0 }}>
          <div style={sectionLabel("")}>TRIGGER</div>
          <Prose html={a.trigger} />
        </div>
      )}
      {a.detailHtml && <Prose html={a.detailHtml} />}
      {a.effectHtml && <Prose html={a.effectHtml} style={{ marginTop: a.detailHtml ? 8 : 0 }} />}
    </div>
  );
}

/** A small labelled cell in a stat strip — the shared shape for weapon and deployable numbers. */
function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ minWidth: 62 }}>
      <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1.4, color: "#888", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#111" }}>{value}</div>
    </div>
  );
}

/**
 * The weapon stat block. For 54 features in EACH shipped pack this is the whole record — their
 * `effect` is empty or absent and every fact about them is in these numbers, which is why they
 * used to render as a blank sheet.
 */
function NpcWeaponBlock({ w }: { w: NpcWeaponView }) {
  const cells: { label: string; value: string }[] = [];
  if (w.attackBonus) cells.push({ label: "ATTACK", value: w.attackBonus });
  if (w.accuracy) cells.push({ label: "ACCURACY", value: w.accuracy });
  if (w.attacks) cells.push({ label: "ATTACKS", value: w.attacks });
  for (const dmg of w.damage) {
    // Damage reads as one fact ("4 Explosive"), so type and number share a cell rather than
    // splitting across two that a reader has to recombine.
    const suffix = [dmg.aoe, dmg.ap ? "AP" : undefined].filter(Boolean).join(" ");
    cells.push({
      label: `DAMAGE${suffix ? ` · ${suffix.toUpperCase()}` : ""}`,
      value: [dmg.value, dmg.type].filter(Boolean).join(" "),
    });
  }
  for (const r of w.range) cells.push({ label: "RANGE", value: r.label });
  return (
    <div style={{ padding: "13px 18px", borderBottom: "2px solid #111" }}>
      <div style={sectionLabel(w.weaponType ? "" : "", "#c0240f")}>
        {w.weaponType ? w.weaponType.toUpperCase() : "WEAPON"}
      </div>
      {cells.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginBottom: w.onHitHtml || w.onCritHtml ? 12 : 0 }}>
          {cells.map((c, i) => (
            <StatCell key={`${c.label}-${i}`} label={c.label} value={c.value} />
          ))}
        </div>
      )}
      {w.onHitHtml && (
        <div style={{ marginTop: 4 }}>
          <div style={sectionLabel("")}>ON HIT</div>
          <Prose html={w.onHitHtml} />
        </div>
      )}
      {w.onCritHtml && (
        <div style={{ marginTop: 8 }}>
          <div style={sectionLabel("")}>ON CRIT</div>
          <Prose html={w.onCritHtml} />
        </div>
      )}
    </div>
  );
}

/**
 * One deployable. 13 v3 features have their entire content here and nothing else — no effect, no
 * actions, no weapon block of their own — so without this they render completely empty.
 */
function NpcDeployableBlock({ dep }: { dep: NpcDeployableView }) {
  const meta = [dep.kind, dep.activation, dep.frequency, dep.redeploy].filter((x): x is string => !!x);
  return (
    <div style={{ padding: "13px 18px", borderBottom: "2px solid #111", background: "#f6f4ee" }}>
      <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        {dep.name && (
          <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: 1.5, fontWeight: 700 }}>
            {dep.name.toUpperCase()}
          </div>
        )}
        {meta.map((m) => (
          <span
            key={m}
            style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: "#555", border: "1px solid #bbb", padding: "2px 6px" }}
          >
            {m.toUpperCase()}
          </span>
        ))}
      </div>
      {dep.stats.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginBottom: dep.detailHtml || dep.weapon ? 10 : 0 }}>
          {dep.stats.map((st) => (
            <StatCell key={st.label} label={st.label} value={st.value} />
          ))}
        </div>
      )}
      {dep.detailHtml && <Prose html={dep.detailHtml} />}
      {dep.weapon && (
        <div style={{ marginTop: 10, borderTop: "1px solid #ddd", paddingTop: 8 }}>
          <div style={sectionLabel("")}>{(dep.weapon.weaponType ?? "ATTACK").toUpperCase()}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 18 }}>
            {dep.weapon.attackBonus && <StatCell label="ATTACK" value={dep.weapon.attackBonus} />}
            {dep.weapon.damage.map((dm, i) => (
              <StatCell key={i} label="DAMAGE" value={[dm.value, dm.type].filter(Boolean).join(" ")} />
            ))}
            {dep.weapon.range.map((r, i) => (
              <StatCell key={`r${i}`} label="RANGE" value={r.label} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NpcFeatureBody({ d, onNav }: { d: Detail; onNav: (ref: Ref) => void }) {
  return (
    <>
      {d.effectHtml && (
        <div style={{ padding: "15px 18px", borderBottom: "2px solid #111" }}>
          <div style={sectionLabel("", "#c0240f")}>EFFECT</div>
          <Prose html={d.effectHtml} />
        </div>
      )}
      {/* v3 keeps a feature's rules text here rather than in `effect`; for 171 of the shipped
          pack's 401 features this is the ONLY body they have. */}
      {/* "MODIFIES →" — the feature this one changes. 6 of the 38 records that carry `mod` never
          name their target in prose at all, so without this the relationship is invisible. */}
      {d.npcModifies && (
        <div style={{ padding: "13px 18px", borderBottom: "2px solid #111" }}>
          <div style={sectionLabel("")}>MODIFIES</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <LinkChip it={d.npcModifies} onNav={onNav} />
          </div>
        </div>
      )}
      {d.npcActions?.map((a, i) => <NpcActionBlock key={`${a.name ?? "action"}-${i}`} a={a} />)}
      {d.npcWeapon && <NpcWeaponBlock w={d.npcWeapon} />}
      {d.npcDeployables?.map((dep, i) => <NpcDeployableBlock key={`${dep.name ?? "deployable"}-${i}`} dep={dep} />)}
      <TagRow d={d} onNav={onNav} width={104} />
      {d.descHtml && (
        <div style={{ padding: "15px 18px" }}>
          <Prose html={d.descHtml} />
        </div>
      )}
    </>
  );
}

// ---- self-contained reader ----------------------------------------------------------

/**
 * `<CompendiumRecordReader>` — a standalone record sheet for ONE compendium ref, for hosts that
 * want the archive's rich detail without the whole window (the editor's "＋ add" pickers). It
 * builds the {@link Detail} from `item` and renders {@link DetailPane}. It never mutates anything —
 * read-only, exactly like the archive.
 *
 * **Cross-links.** Without `onNavigate`, the reader is self-contained: a cross-reference link (a
 * maker, a tag, a license's unlocked gear) re-points the sheet **in place** for read-only browsing,
 * resetting whenever the host points at a different `item`. With `onNavigate`, the host owns the
 * links instead — a click hands the ref out (e.g. to open the full COMPENDIUM archive at that
 * record) and the sheet stays on the host's `item`. A picker passes `onNavigate` because it has no
 * back/forward chrome to make in-place navigation legible — wandering off the picked record would
 * be a dead end.
 */
export function CompendiumRecordReader({ source, item, onNavigate }: { source: CompendiumSource; item: Ref; onNavigate?: (ref: Ref) => void }) {
  const [navRef, setNavRef] = useState<Ref | null>(null);
  // Item 3.3: the reader gets the same tier selector the archive window has. It holds its own
  // state rather than inheriting the window's — the two surfaces are opened independently (the
  // encounter-builder pickers mount this one), and a GM reading a T3 record in a picker should
  // not silently retune the archive behind it.
  const [tier, setTier] = useState<NpcTier>(1);
  // A fresh selection from the host clears any in-reader cross-link preview. (Inert under
  // `onNavigate`, where `navRef` is never set and the sheet always shows `item`.)
  useEffect(() => setNavRef(null), [item.type, item.id]);
  const detail = buildDetail(source, navRef ?? item, tier);
  if (!detail) {
    return <div className="be-empty" style={{ padding: "10px 12px" }}>— no record —</div>;
  }
  return <DetailPane d={detail} onNav={onNavigate ?? setNavRef} tier={{ value: tier, onTier: setTier }} />;
}
